import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLazyFilesAdvancedSearchQuery } from '../../api/file.js';
import AuthLayout from '../../components/layout/AuthLayout';
import BaseCard from '../../components/misc/BaseCard';
import BaseInput from '../../components/form/BaseInput';
import BaseButton from '../../components/form/BaseButton';
import AdvancedSearchFilterFiles from '../../components/misc/AdvancedSearchFilterFiles';
import { useDebounce } from '../../hooks/useDebounce';
import ThreeDotsLoader from '../../components/misc/ThreeDotsLoader';
import { useSelector } from 'react-redux';
import useCurrentUser from '../../hooks/useCurrentUser.js';
import IconDownload from '../../components/images/IconDownload.jsx';
import IconEditCompany from '../../components/images/IconEditCompany.jsx';
import IconMP3 from '../../components/images/IconMP3.jsx';
import IconPlay from '../../components/images/IconPlay.jsx';
import IconDocument from '../../components/images/IconDocument.jsx';
import IconSpreadsheet from '../../components/images/IconSpreadsheet.jsx';
import { downloadFile } from '../../utils/file.js';
import FilePreview from '../../components/misc/FilePreview.jsx';
import { useNavigate } from 'react-router-dom';
import { PERMISSION_FILES } from '../../utils/constants.js';
import { renderFieldWithHighlighting } from '../../utils/highlight.js';

const AdvancedSearchFiles = () => {
    const organizationSwitch = useSelector((state) => state.organizationSwitch);
    const { user, permissions: userPermissions } = useCurrentUser();
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);
    const [filesByCategory, setFilesByCategory] = useState({});
    const [search, setSearch] = useState('');
    const [filters, setFilters] = useState({
        types: [],
        dateRange: null,
        countries: []
    });
    const [showFilters, setShowFilters] = useState(false);
    const debouncedSearch = useDebounce(search, 500);
    const [visible, setVisible] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [downloading, setDownloading] = useState(false);
    const [fileCounts, setFileCounts] = useState({});

    const [advancedSearch] = useLazyFilesAdvancedSearchQuery();

    const resetSearchAndFilters = () => {
        setSearch('');
        setFilters({
            types: [],
            dateRange: null,
            countries: []
        });
        setFilesByCategory({});
    };

    const countValid = Object.values(filters).reduce((count, value) => {
        if (Array.isArray(value)) return value.length > 0 ? count + 1 : count;
        return value ? count + 1 : count;
    }, 0);

    const requestParams = useMemo(() => {
        const privateOrg = organizationSwitch[user?.id]?.private || null;
        const organizations = Object.keys(organizationSwitch[user?.id]?.organizations || {}).filter(
            (key) => organizationSwitch[user?.id].organizations[key]
        );

        return {
            search: debouncedSearch,
            ...(filters.types.length > 0 ? { type: filters.types } : {}),
            ...(filters.countries.length > 0 ? { countries: filters.countries } : {}),
            ...(filters.dateRange?.[0] ? { start_date: filters.dateRange[0].toISOString().split('T')[0] } : {}),
            ...(filters.dateRange?.[1] ? { end_date: filters.dateRange[1].toISOString().split('T')[0] } : {}),
            ...(organizations.length > 0 ? { organizations } : {}),
            private: privateOrg
        };
    }, [debouncedSearch, filters, organizationSwitch, user?.id]);

    const loadMoreFiles = async (categoryId) => {
        const currentFiles = filesByCategory[categoryId] || [];
        const currentPage = Math.ceil(currentFiles.length / 3) + 1;

        try {
            const result = await advancedSearch({
                ...requestParams,
                category: categoryId,
                page: currentPage,
                per_page: 3
            }).unwrap();

            const newFiles = result?.response?.files || [];

            const mergedFiles = [
                ...currentFiles,
                ...newFiles.filter((newFile) => !currentFiles.some((file) => file.id === newFile.id))
            ];

            setFilesByCategory((prev) => ({
                ...prev,
                [categoryId]: mergedFiles
            }));

            setFileCounts((prev) => ({
                ...prev,
                [categoryId]: {
                    count: mergedFiles.length,
                    has_more_pages: result.response.has_more_pages
                }
            }));
        } catch (error) {
            console.error(`Error loading more files for category ${categoryId}:`, error);
        }
    };

    const applyFilters = useCallback(async () => {
        if (requestParams.private || requestParams.organizations?.length > 0) {
            setIsLoading(true);
            try {
                const result = await advancedSearch(requestParams).unwrap();

                const groupedFiles = result?.response?.files.reduce((acc, file) => {
                    const category = file.category || 'Uncategorized';
                    if (!acc[category]) acc[category] = [];
                    acc[category].push(file);
                    return acc;
                }, {});

                const initialFileCounts = Object.keys(groupedFiles).reduce((acc, category) => {
                    acc[category] = {
                        count: groupedFiles[category].length,
                        has_more_pages: result.response.has_more_pages
                    };
                    return acc;
                }, {});

                setFilesByCategory(groupedFiles || {});
                setFileCounts(initialFileCounts);
            } catch (error) {
                console.error('Error during search:', error);
            }
            setIsLoading(false);
        }
    }, [advancedSearch, requestParams]);

    useEffect(() => {
        if (debouncedSearch) {
            setFilesByCategory({});
            applyFilters();
        }
    }, [debouncedSearch, applyFilters]);

    const handleDownload = (file) => {
        downloadFile(file.url, file.title, file.extension, setDownloading);
    };

    const handlePreview = (file) => {
        setSelectedFile(file);
        setVisible(true);
    };

    const renderFileDisplay = (file) => {
        const { category, url } = file;

        if (category === 'media') {
            if (url.match(/\.(jpg|jpeg|png|gif|webp|svg|tif|tiff|bmp|heic|heif)$/)) {
                return (
                    <div className='h-[107px] bg-theme1-white rounded-t-2xl shadow-md'>
                        <img src={url} alt='Media File' className='h-full w-full object-cover rounded-t-2xl' />
                    </div>
                );
            } else if (url.match(/\.(mp3|wav)$/)) {
                return (
                    <div className='h-[107px] bg-theme1-tan rounded-t-2xl flex items-center justify-center'>
                        <IconMP3 className='w-11 h-11 fill-theme1-primary' />
                    </div>
                );
            } else if (url.match(/\.(mp4|avi)$/)) {
                return (
                    <div className='h-[107px] bg-theme1-tan rounded-t-2xl flex items-center justify-center'>
                        <IconPlay className='w-11 h-11 stroke-theme1-primary' />
                    </div>
                );
            }
        } else if (category === 'document') {
            return (
                <div className='h-[107px] bg-theme1-tan bg-opacity-[85%] rounded-t-2xl flex items-center justify-center'>
                    <IconDocument className='w-11 h-11 stroke-theme1-primary' />
                </div>
            );
        } else if (category === 'spreadsheet') {
            return (
                <div className='h-[107px] bg-theme1-tan rounded-t-2xl flex items-center justify-center'>
                    <IconSpreadsheet className='w-11 h-11 stroke-theme1-primary' />
                </div>
            );
        }
    };

    const renderFiles = (categoryId) => {
        const files = filesByCategory[categoryId] || [];
        const hasMoreFiles = fileCounts[categoryId]?.has_more_pages;

        return (
            <div key={categoryId} className='mb-8'>
                <h2 className='text-theme1-primary2 font-bold text-2xl mb-4 capitalize'>{categoryId}</h2>
                <div className='grid grid-cols-3 sm:grid-cols-4 gap-4'>
                    {files.map((file) => (
                        <div
                            key={file.id}
                            className='border border-theme1-cardStroke bg-theme1-white rounded-2xl text-theme1-text1 flex flex-col relative'
                        >
                            <div onClick={() => handlePreview(file)}>{renderFileDisplay(file)}</div>
                            <div className='p-4'>
                                <h3 className='text-sm font-bold truncate'>
                                    {renderFieldWithHighlighting(file.title, 'title', file.matched_fields)}
                                </h3>
                                <p className='text-sm text-theme1-text2 truncate'>
                                    {renderFieldWithHighlighting(file.description, 'description', file.matched_fields)}
                                </p>
                            </div>
                            <div className='flex justify-between p-4'>
                                <button
                                    type='button'
                                    onClick={() => handleDownload(file)}
                                    className='cursor-pointer'
                                    disabled={downloading}
                                >
                                    <IconDownload className='w-8 h-8 fill-theme1-text3' />
                                </button>
                                {!!userPermissions[PERMISSION_FILES].update && (
                                    <button
                                        onClick={() => navigate(`/files/edit/${file.id}`)}
                                        className='cursor-pointer'
                                    >
                                        <IconEditCompany className='w-9 h-9 fill-theme1-text3' />
                                    </button>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
                {files.length > 0 && hasMoreFiles && (
                    <div className='mt-4 text-center'>
                        <button
                            onClick={() => loadMoreFiles(categoryId)}
                            className='bg-none border-none text-theme1-title2 font-bold p-0 text-lg'
                            disabled={isLoading}
                        >
                            Load More
                        </button>
                    </div>
                )}
            </div>
        );
    };

    return (
        <AuthLayout title='Advanced Search' backRoute='/files'>
            <BaseCard className='border-0 m-0 mb-6 -mt-6 p-4 pt-10 rounded-t-none bg-theme1-white shadow-lg max-w-3xl w-full mx-auto'>
                <div className='max-w-3xl w-full mx-auto px-4'>
                    <div className='flex justify-between items-center mt-6 mb-4'>
                        <div className='flex-grow'>
                            <BaseInput
                                name='search'
                                type='text'
                                value={search}
                                placeholder='Search... (min 3 characters)'
                                setValue={setSearch}
                            />
                        </div>
                    </div>
                </div>
                <div className='mb-6 px-4 text-right'>
                    <div className='flex items-center w-full text-theme1-primary2'>
                        <div className='flex items-center space-x-3'>
                            <BaseButton
                                className='flex bg-theme1-lightBtns text-theme1-white p-3 pb-[10px]'
                                onClick={() => setShowFilters(true)}
                            >
                                <svg
                                    xmlns='http://www.w3.org/2000/svg'
                                    width='23'
                                    height='23'
                                    viewBox='0 0 20 20'
                                    fill='none'
                                >
                                    <path
                                        fillRule='evenodd'
                                        clipRule='evenodd'
                                        d='M4.09254 3.2116C4.17673 3.18208 4.26531 3.16699 4.35454 3.16699H14.6462C14.7354 3.16699 14.824 3.18208 14.9082 3.2116C15.1362 3.29158 15.3429 3.42268 15.5125 3.59487C15.682 3.76706 15.8099 3.97578 15.8863 4.20502C15.9627 4.43426 15.9856 4.67795 15.9533 4.91743C15.921 5.15691 15.8343 5.38581 15.6999 5.5866C15.6782 5.61903 15.6541 5.64981 15.6278 5.67869L11.8754 9.80639V15.042C11.8754 15.3419 11.706 15.616 11.4377 15.7501C11.1695 15.8842 10.8486 15.8552 10.6087 15.6753L7.44204 13.3003C7.24269 13.1508 7.12537 12.9162 7.12537 12.667V9.80639L3.37292 5.67869C3.34666 5.64981 3.32258 5.61903 3.30087 5.5866C3.16643 5.38581 3.07973 5.15691 3.04742 4.91743C3.01511 4.67796 3.03804 4.43427 3.11445 4.20502C3.19087 3.97578 3.31874 3.76706 3.48828 3.59487C3.65781 3.42268 3.86451 3.29157 4.09254 3.2116ZM4.66876 4.75033L8.50282 8.96779C8.6353 9.11352 8.7087 9.30339 8.7087 9.50033V12.2712L10.292 13.4587V9.50033C10.292 9.30339 10.3654 9.11352 10.4979 8.96779L14.332 4.75033H4.66876Z'
                                        fill='white'
                                    />
                                </svg>
                                <span className='text-sm text-theme1-white px-2'>{countValid} filter(s) applied</span>
                            </BaseButton>
                        </div>

                        <BaseButton
                            className='ml-auto w-min rounded-4xl border font-normal text-theme1-white border-theme1-lightBtns px-4 py-3 bg-theme1-lightBtns'
                            onClick={resetSearchAndFilters}
                        >
                            Reset
                        </BaseButton>
                    </div>
                </div>
            </BaseCard>
            <AdvancedSearchFilterFiles
                filters={filters}
                setFilters={setFilters}
                visible={showFilters}
                setVisible={setShowFilters}
                applyFilters={applyFilters}
            />
            <div className='max-w-3xl w-full mx-auto px-4'>
                {isLoading ? (
                    <ThreeDotsLoader />
                ) : Object.keys(filesByCategory).length > 0 ? (
                    Object.keys(filesByCategory).map((category) => renderFiles(category))
                ) : (
                    <div>No files found.</div>
                )}
            </div>
            {selectedFile && (
                <FilePreview
                    file={selectedFile}
                    visible={visible}
                    setVisible={setVisible}
                    setSelectedFile={setSelectedFile}
                    search={search}
                />
            )}
        </AuthLayout>
    );
};

export default AdvancedSearchFiles;
